"use client";

import { Icon } from "@ospweb/sdk-ui";

import { useKeyboardInsets } from "@hooks/useKeyboardInsets";
import { VerifyEmailPageLayout } from "@components/EmailLogin/layouts/VerifyEmailPageLayout";
import { LoadingButton } from "@components/EmailLogin/LoadingButton";

import { InviteCodeInputField } from "./InviteCodeInputField";
import { createInviteCodeCopy } from "./inviteCodeCopy";
import { useInviteCodeApply } from "./useInviteCodeApply";

/**
 * 邀请码全屏页 —— Figma `104980:320350`（默认）/ `104980:320411`（错误）。
 *
 * **H5 专用**，作为登录流的 `invite` step 渲染（不是独立路由）：
 *
 * ```
 * 登录页 email step → 点底部邀请码行 → invite step（本组件）
 *                   ← 返回箭头 / Apply code 成功
 * ```
 *
 * ## 为什么是「登录流内一屏」而不是独立路由
 *
 * 视觉照旧是这份全屏页稿（H5 垂直空间不够做折叠 —— 见 `InviteCodeSection`），
 * 但承载改成 step 切换，为的是**不卸载 `useEmailLoginLogic`**：走独立路由时页面整体卸载，
 * 用户已填的邮箱会丢，回来得重填。
 *
 * 这也与原生对齐 —— 原生 `LoginScreen.kt` 同样是登录屏内 `stage` 切换、不进导航栈：
 * ```kotlin
 * if (stage == "invite") { InviteCodeStage(service, onBack = { stage = "email" }); return }
 * ```
 * 代价是浏览器物理返回不再从本屏回登录页（要点页内返回箭头），与原生行为一致。
 *
 * 复用 `VerifyEmailPageLayout`：同背景 token、同 353 内容列、同 363px 底部装饰与键盘收起行为。
 * 判定逻辑见 {@link useInviteCodeApply}（与 PC 折叠壳同一份）。
 */
interface InviteCodeStageViewProps {
  /** 返回邮箱步骤（返回箭头与提交成功共用） */
  onBack: () => void;
  locale?: string;
}

export function InviteCodeStageView({
  onBack,
  locale,
}: InviteCodeStageViewProps) {
  const copy = createInviteCodeCopy(locale);
  const keyboardInsets = useKeyboardInsets();
  const invite = useInviteCodeApply({ onDone: onBack });

  return (
    <VerifyEmailPageLayout
      collapseDecor={keyboardInsets.visible}
      headerSlot={
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          data-openreplay-label="invitecode_page_back_click"
          data-testid="invite-code-back"
          className="flex size-10 shrink-0 items-center justify-center rounded-full drop-shadow-[0px_0px_16px_rgba(0,0,0,0.08)]"
        >
          <Icon
            name="left-arrow-alt"
            size={20}
            className="text-content_primary"
          />
        </button>
      }
      footerSlot={
        <LoadingButton
          type="button"
          disabled={!invite.canSubmit}
          loading={invite.checking}
          onClick={() => void invite.apply()}
          data-openreplay-label="invitecode_page_apply_click"
          data-testid="invite-code-apply"
          className="button md_button primary_button button_l flex h-12 w-full items-center justify-center rounded-lg text-content_inverse_primary"
        >
          {copy.applyCode}
        </LoadingButton>
      }
    >
      <div
        className="flex w-full flex-col gap-8"
        data-figma-node="104980:320352"
      >
        <div className="flex w-full flex-col gap-3">
          <h1 className="headline_regular_m text-content_primary">
            {copy.title}
          </h1>
          <p className="body_medium_m text-content_secondary">
            {copy.subtitle}
          </p>
        </div>

        <InviteCodeInputField
          value={invite.value}
          onValueChange={invite.onValueChange}
          error={invite.error}
          locale={locale}
        />
      </div>
    </VerifyEmailPageLayout>
  );
}
